import React from 'react'
import { CONTACT_EMAIL } from '../../Constants'
import LegalPageLayout from './LegalPageLayout'

const sections = [
  {
    title: '1. Eligibility',
    blocks: [
      {
        type: 'paragraph',
        text: 'You must be at least 13 years old to use Anear, or older if required by the laws where you live. If you use Anear on behalf of a company, organization, merchant, venue, or other entity, you represent that you have authority to bind that entity to these Terms.'
      }
    ]
  },
  {
    title: '2. The Service',
    blocks: [
      {
        type: 'paragraph',
        text: 'Anear provides location-aware and in-person digital experiences, including event participation, hosting tools, venue tools, merchant tools, developer tools, and related services. We may add, remove, or modify features at any time.'
      }
    ]
  },
  {
    title: '3. Accounts and Guest Access',
    blocks: [
      {
        type: 'paragraph',
        text: 'You may use some parts of Anear through a registered account or, where available, as a guest. You are responsible for providing accurate information, maintaining the security of your account or device, and all activity that occurs under your account or guest session.'
      },
      {
        type: 'paragraph',
        text: `If you believe your account has been compromised, please contact us promptly at ${CONTACT_EMAIL}.`
      }
    ]
  },
  {
    title: '4. Acceptable Use',
    blocks: [
      {
        type: 'list',
        items: [
          'Do not violate any law or regulation.',
          'Do not impersonate another person or misrepresent your affiliation.',
          'Do not interfere with or disrupt the service or its infrastructure.',
          'Do not attempt to gain unauthorized access to accounts, systems, or data.',
          'Do not use the service to harass, abuse, threaten, or harm others.',
          'Do not upload or transmit malicious code, spam, or fraudulent content.',
          'Do not exploit location, participation, or identity features in deceptive or abusive ways.',
          'Do not reverse engineer, copy, scrape, or misuse the service except as allowed by law.'
        ]
      },
      {
        type: 'paragraph',
        text: 'We may suspend or terminate access for behavior that violates these Terms or creates risk for Anear or other users.'
      }
    ]
  },
  {
    title: '5. User Content',
    blocks: [
      {
        type: 'paragraph',
        text: 'You may submit content through Anear, including names, profile materials, responses, messages, uploads, and event-related content. You retain ownership of your content, but you grant Anear a worldwide, non-exclusive, royalty-free license to host, store, reproduce, modify as needed for formatting and display, transmit, and display that content for the purpose of operating, improving, and promoting the service.'
      },
      {
        type: 'paragraph',
        text: 'You represent that you own or have the rights necessary to submit your content, that your content does not violate the rights of any person or entity, and that your content does not violate any law or these Terms. We may remove or restrict content that we reasonably believe violates these Terms or creates legal or operational risk.'
      }
    ]
  },
  {
    title: '6. Event, Host, and Merchant Responsibility',
    blocks: [
      {
        type: 'paragraph',
        text: 'If you create, host, organize, or operate events, venues, or merchant experiences through Anear, you are responsible for the accuracy of the information you provide, your conduct toward participants, obtaining any permissions or consents needed for your event or venue, and complying with applicable laws.'
      },
      {
        type: 'paragraph',
        text: 'Anear provides the platform but is not the organizer, operator, or sponsor of third-party events unless explicitly stated otherwise.'
      }
    ]
  },
  {
    title: '7. Location-Based Features',
    blocks: [
      {
        type: 'paragraph',
        text: 'Some Anear features depend on location information to support in-person participation, venue experiences, or proximity-based access. By using those features, you agree that location information may be used to verify participation eligibility and that inaccurate or disabled location settings may prevent access to certain events.'
      },
      {
        type: 'paragraph',
        text: 'Anear is not responsible if a user is unable to join or use a feature because location access is unavailable, denied, or inaccurate.'
      }
    ]
  },
  {
    title: '8. Paid Services',
    blocks: [
      {
        type: 'paragraph',
        text: 'If Anear offers paid subscriptions, merchant services, developer services, or other paid features, pricing and billing terms will be presented at the time of purchase. Fees are due as stated and may be non-refundable except as required by law. We may suspend paid features for nonpayment.'
      }
    ]
  },
  {
    title: '9. Intellectual Property',
    blocks: [
      {
        type: 'paragraph',
        text: 'Anear and its related software, designs, branding, text, graphics, interfaces, and other content are owned by us or our licensors and are protected by intellectual property laws. Except as expressly allowed by these Terms, you may not copy, modify, distribute, sell, lease, reverse engineer, or create derivative works from the service.'
      }
    ]
  },
  {
    title: '10. Termination',
    blocks: [
      {
        type: 'paragraph',
        text: 'You may stop using Anear at any time. We may suspend or terminate your access, with or without notice, if you violate these Terms, if we believe your conduct creates legal exposure, security risk, or harm to other users, or if we discontinue the service or a portion of it.'
      }
    ]
  },
  {
    title: '11. Disclaimers',
    blocks: [
      {
        type: 'paragraph',
        text: 'Anear is provided on an "as is" and "as available" basis. To the maximum extent permitted by law, Anear disclaims all warranties, express or implied, including warranties of merchantability, fitness for a particular purpose, title, and non-infringement. We do not guarantee that the service will be uninterrupted, error-free, secure, or available at all times.'
      }
    ]
  },
  {
    title: '12. Limitation of Liability',
    blocks: [
      {
        type: 'paragraph',
        text: 'To the maximum extent permitted by law, Anear and its officers, directors, employees, affiliates, licensors, and service providers will not be liable for any indirect, incidental, special, consequential, exemplary, or punitive damages, or for any loss of profits, revenues, goodwill, data, or business opportunities arising out of or related to your use of the service.'
      },
      {
        type: 'paragraph',
        text: 'To the maximum extent permitted by law, Anear\'s total liability for any claim arising out of or relating to the service will not exceed the greater of the amount you paid us in the 12 months before the claim arose or USD $100. Some jurisdictions do not allow certain limitations of liability, so some of the above may not apply to you.'
      }
    ]
  },
  {
    title: '13. Indemnification',
    blocks: [
      {
        type: 'paragraph',
        text: 'You agree to defend, indemnify, and hold harmless Anear and its affiliates, officers, directors, employees, licensors, and service providers from and against claims, liabilities, damages, losses, and expenses, including reasonable attorneys\' fees, arising out of or related to your use of the service, your content, your events or venue operations, or your violation of these Terms or applicable law.'
      }
    ]
  },
  {
    title: '14. Governing Law',
    blocks: [
      {
        type: 'paragraph',
        text: 'These Terms are governed by the laws of the applicable jurisdiction determined by Anear Corporation, without regard to conflict of laws principles. Any dispute arising from these Terms or the service will be resolved in the courts selected by Anear Corporation, except where applicable law requires otherwise.'
      }
    ]
  },
  {
    title: '15. Changes to These Terms',
    blocks: [
      {
        type: 'paragraph',
        text: 'We may revise these Terms from time to time. If we make material changes, we will post the updated Terms here and update the effective date. By continuing to use Anear after the updated Terms become effective, you agree to the revised Terms.'
      }
    ]
  }
]

const TermsOfServicePage = () => {
  return (
    <LegalPageLayout
      title="Terms of Service"
      effectiveDate="March 23, 2026"
      intro="These Terms of Service govern your access to and use of Anear.me and any related websites, applications, content, features, and services provided by Anear Corporation. By accessing or using Anear, you agree to these Terms. If you do not agree, do not use the service."
      sections={sections}
      contactEmail={CONTACT_EMAIL}
      companyName="Anear Corporation"
    />
  )
}

export default TermsOfServicePage
